import React from 'react';
import { getContext, getDuration, isAnimationDisabled } from 'common';
import Menu, { MenuProps, MenuRef } from 'components/Menu';
import { useTransition, animated } from 'react-spring';
import TOKENS from '@nokia-csf-uxr/freeform-design-tokens';
import _merge from 'lodash/merge';
import Styled from './styles';

const DEFAULT_ANIMATION = immediate => ({
  from: { opacity: TOKENS.OPACITY.OPACITY_0 },
  enter: { opacity: 1 },
  leave: { opacity: TOKENS.OPACITY.OPACITY_0 },
  config: { duration: getDuration(TOKENS.SPEED.SPEED_NORMAL) },
  immediate
});

export interface SignInPasswordPopupProps extends React.ComponentPropsWithRef<'div'> {
  /** Place content here. */
  children: React.ReactNode;
  /** Toggles popup visibility. */
  isOpen?: boolean;
  /** Override the props used to position this popup. */
  menuProps?: Partial<MenuProps>;
  /** Control the animation. Leverages 'react-spring' useSpring/useTransition. See https://react-spring.io/ */
  animationProps?: any;
  /** If true, disable all motion animation for this component.  This property will override any global disableAnimation setting. */
  disableAnimation?: boolean;
  /** To override the theme please refer to the "Readme" tab to learn more. */
  theme?: any;
  /** To customize the wrapper style. */
  style?: React.CSSProperties
}

/** Place main Sign In footer content here (eg. copyright info) */
const SignInPasswordPopup = React.forwardRef<HTMLElement, SignInPasswordPopupProps>((props: SignInPasswordPopupProps, ref) =>
  getContext(({ theme: globalTheme, disableAnimation }) => {
    const {
      children, isOpen, animationProps, disableAnimation: propsdisableAnimation, menuProps, theme, style, ...otherProps
    } = props;
    const [triggerEl, setTriggerEl] = React.useState(undefined);
    const menuRef = React.useRef<MenuRef>(null);
    const transitions = useTransition(isOpen, null, animationProps || DEFAULT_ANIMATION(isAnimationDisabled(propsdisableAnimation, disableAnimation)));
    const resolvedTheme = _merge({}, globalTheme.SIGN_IN, theme);

    // adjust popup width
    React.useLayoutEffect(() => {
      if (menuProps.triggerEl && menuProps.triggerEl.current) {
        setTriggerEl(menuProps.triggerEl.current.getBoundingClientRect());
      }
    }, [menuProps.triggerEl]);

    // force popper to update now that menu is showing (for more accurate placement)
    React.useLayoutEffect(() => {
      if (isOpen) {
        menuRef.current && menuRef.current.positionMenu();
      }
    }, [isOpen]);

    return (
      <Menu
        ref={menuRef}
        keepMounted
        usePortal={false}
        positioningOptions={{
          placement: 'bottom-start'
        }}
        {...menuProps}
      >
        {transitions.map(({ item, key, props: transitionStyles }) =>
          item && (
            <Styled.SignInPasswordPopup
              ref={ref}
              as={animated.div}
              key={key}
              theme={resolvedTheme}
              style={_merge({}, { width: triggerEl && triggerEl.width, ...transitionStyles }, style)}
              {...otherProps}
            >
              {children}
            </Styled.SignInPasswordPopup>
          ))}
      </Menu>
    );
  }));

SignInPasswordPopup.defaultProps = {
  animationProps: undefined,
  disableAnimation: undefined,
  menuProps: undefined,
  isOpen: false,
  theme: {},
  style: {}
};

SignInPasswordPopup.displayName = 'SignInPasswordPopup';

/* Exports */
export { SignInPasswordPopup as default };
